/**
 * 追爆仓第六出口「我来出镜拍这条」的交接包(0924 车 D,契约 §3.4)。
 *
 * ═══ 为什么只带套路投影 ═══════════════════════════════════════════════════════════
 * 老板 0924 拍板①:模板与追爆都**只给脚本**,不把视频当换人参考。追爆→真人出镜这座桥
 * 只运「套路」(结构/节拍/拍法/话术公式),**原片一个像素、一个字都不出境**:
 *   · 不带 `breakdown`(拆解报告里有逐字 captions / 参考产品卖点);
 *   · 不带 `source.rawVideoKey` / `hasRawVideo`(原片指针,真人线绝不拿它当参考);
 *   · payload 里去掉 `transcript`(逐字口播)与每张场景卡的 `onScreenText`(原片屏幕字)。
 *
 * ★投影口径逐字镜像 api `ExplosiveBlueprintSanitizer.forAgentGeneration`(军师读
 *   `/internal/explosive/blueprints/{id}/formula` 走的同一份「完整结构、零逐字」规矩):
 *   顶层 PAYLOAD_KEYS 白名单浅投影 → 剔 VERBATIM_KEYS(transcript)→ 场景卡白名单深投影
 *   再剔 SCENE_CARD_VERBATIM_KEYS(onScreenText)→ 缺命脉字段(beats15Raw / copyFormula)
 *   返回 null(fail-closed:发一份残缺套路过去,ai 只会写出一份空洞脚本)。
 *   api 那边白名单加字段时这里要同步(两处同一个知识,头注互指)。
 *
 * ★放在 entities 层:出口在 `features/explosive`、消费在 `features/video`,
 *   features 互不 import(ADR-0013),共享的形状与投影只能下沉到这里。
 */
import type { ExplosiveBlueprintDto, FormulaPayload } from "./types";

/** 真人出镜专属页路由(与 App.tsx 的 path 同值)。 */
export const REAL_PERSON_ROUTE = "/ai-video/real";

/** api `PAYLOAD_KEYS` 减去 `VERBATIM_KEYS`(transcript)。 */
const FORMULA_KEYS = [
  "key",
  "name",
  "purposes",
  "when",
  "beats15",
  "beats15Raw",
  "beats30Raw",
  "copyFormula",
  "slots",
  "fit",
  "conflict",
  "pitfalls",
  "b2b",
  "cta",
  "misc",
  "kind",
  "sceneCards",
] as const;

/** api `SCENE_CARD_KEYS` 减去 `SCENE_CARD_VERBATIM_KEYS`(onScreenText)。 */
const SCENE_CARD_KEYS = [
  "beat",
  "timeRange",
  "sceneType",
  "shotSize",
  "cameraMove",
  "subject",
  "action",
  "visual",
  "mood",
  "transition",
  "sound",
] as const;

/** 零逐字的套路投影(送 ai `POST /video/real/script` 的 `blueprintFormula`)。 */
export type RealPersonFormula = Record<string, unknown>;

export interface RealPersonExplosiveHandoff {
  blueprintId: number;
  formulaName: string;
  /** 零逐字套路投影 —— 见头注。 */
  formula: RealPersonFormula;
  /** 来源条「来自 抖音」展示用(溯源元数据,不是内容)。 */
  sourcePlatform?: string;
}

function isNonBlankText(v: unknown): boolean {
  return typeof v === "string" && v.trim().length > 0;
}

/**
 * 蓝图 payload → 零逐字套路投影;缺命脉字段 → null。
 * 纯函数,不改入参。
 */
export function projectFormulaForScript(
  payload: FormulaPayload | null | undefined
): RealPersonFormula | null {
  if (!payload || typeof payload !== "object") return null;
  const out: RealPersonFormula = {};
  for (const k of FORMULA_KEYS) {
    if (!(k in payload) || payload[k] == null) continue;
    if (k === "sceneCards") {
      const cards = payload.sceneCards;
      if (!Array.isArray(cards)) continue;
      out.sceneCards = cards
        .filter((c): c is Record<string, unknown> => !!c && typeof c === "object")
        .map((c) => {
          const card: Record<string, string> = {};
          for (const ck of SCENE_CARD_KEYS) {
            const v = c[ck];
            if (typeof v === "string") card[ck] = v;
          }
          return card;
        });
      continue;
    }
    out[k] = payload[k];
  }
  if (!isNonBlankText(out.beats15Raw) || !isNonBlankText(out.copyFormula)) return null;
  return out;
}

/** 出口侧:蓝图 → 交接包;投影不出(缺命脉)→ null,调用方给人话、不跳转。 */
export function buildRealPersonHandoff(
  bp: Pick<ExplosiveBlueprintDto, "id" | "name" | "payload" | "source">
): RealPersonExplosiveHandoff | null {
  const formula = projectFormulaForScript(bp.payload);
  if (!formula) return null;
  return {
    blueprintId: bp.id,
    formulaName: bp.name,
    formula,
    ...(bp.source?.platform ? { sourcePlatform: bp.source.platform } : {}),
  };
}

/**
 * 消费侧验型:`location.state.explosive` 不可信(刷新/手改 history 都可能带脏值)。
 * ★不信任出口侧已投影 —— 消费侧再过一遍同一个投影(幂等),任何人往 state 里塞了
 *   transcript / onScreenText 也到不了写脚本端点。
 */
export function parseRealPersonHandoff(state: unknown): RealPersonExplosiveHandoff | null {
  if (!state || typeof state !== "object") return null;
  const raw = (state as { explosive?: unknown }).explosive;
  if (!raw || typeof raw !== "object") return null;
  const h = raw as Partial<RealPersonExplosiveHandoff> & { payload?: FormulaPayload };
  if (typeof h.blueprintId !== "number" || !Number.isFinite(h.blueprintId)) return null;
  const formula = projectFormulaForScript((h.formula ?? h.payload) as FormulaPayload | undefined);
  if (!formula) return null;
  return {
    blueprintId: h.blueprintId,
    formulaName: typeof h.formulaName === "string" ? h.formulaName : "爆款蓝图",
    formula,
    ...(typeof h.sourcePlatform === "string" ? { sourcePlatform: h.sourcePlatform } : {}),
  };
}
